import { getDefaultMediaItem } from "./productMedia";

export const resolveBranchId = (stationId) => stationId;

export const isFamilyCategory = (cat) =>
  cat?.isFamily === true || cat?.isFamily === 1 || cat?.isFamily === "true";

/** Normalize product categories from API / Redux into a hierarchy array. */
export const normalizeCategoryHierarchy = (productCategories) => {
  const entry = productCategories?.[0];
  if (!entry) return [];

  if (
    Array.isArray(entry?.categoryHierarchy) &&
    entry.categoryHierarchy.length
  ) {
    return entry.categoryHierarchy;
  }

  if (Array.isArray(entry) && entry.length > 0 && entry[0]?.slug) {
    return entry;
  }

  if (entry?.slug) return [entry];

  return [];
};

export const findFamilyInHierarchy = (hierarchy) => {
  if (!Array.isArray(hierarchy)) return null;
  return hierarchy.find((cat) => isFamilyCategory(cat)) ?? null;
};

export const productIsInFamily = (product) =>
  Boolean(findFamilyInHierarchy(getProductCategoryPath(product)));

/**
 * Map API hierarchy to ProductEditForm levels (מחלקה / קבוצה / תת קבוצה / מקבץ).
 * Supports both leaf-first and root-first hierarchy orderings.
 */
export const getCategoryLevelsFromHierarchy = (hierarchy) => {
  if (!Array.isArray(hierarchy) || !hierarchy.length) {
    return { department: null, group: null, subgroup: null, family: null };
  }

  const familyCat = findFamilyInHierarchy(hierarchy);
  const family = familyCat?.slug ?? null;
  const nonFamily = hierarchy.filter((cat) => !isFamilyCategory(cat));

  const rootFirst =
    nonFamily[0]?.parentSlug === "_root" || nonFamily[0]?.parentSlug === "ROOT";

  const rootToLeaf = rootFirst ? nonFamily : [...nonFamily].reverse();

  return {
    department: rootToLeaf[0]?.slug ?? null,
    group: rootToLeaf[1]?.slug ?? null,
    subgroup: rootToLeaf[2]?.slug ?? null,
    family,
  };
};

export const getPeerCategoryLevels = (productCategories) => {
  const entry = productCategories?.[0];
  const hierarchy = normalizeCategoryHierarchy(productCategories);
  const levels = getCategoryLevelsFromHierarchy(hierarchy);

  if (!levels.family && entry?.isFamily && entry?.slug) {
    return { ...levels, family: entry.slug };
  }

  return levels;
};

/** Deepest assigned level used for API fetch (מקבץ → תת קבוצה → קבוצה → מחלקה). */
export const getPeerCategorySlug = (productCategories) => {
  const levels = getPeerCategoryLevels(productCategories);
  if (
    !levels.department &&
    !levels.group &&
    !levels.subgroup &&
    !levels.family
  ) {
    return null;
  }

  return levels.family || levels.subgroup || levels.group || levels.department;
};

export const findCategoryNodeBySlug = (node, slug) => {
  if (!node || !slug) return null;
  if (node.slug === slug) return node;

  for (const child of node.children || []) {
    const found = findCategoryNodeBySlug(child, slug);
    if (found) return found;
  }

  return null;
};

export const normalizeCategoryProduct = (product) => {
  if (!product) return null;

  const productId =
    product.productId ?? product.product_id ?? product.id ?? product.ProductMkt;
  if (productId == null || productId === "") return null;

  const name = product.name ?? product.ProductName ?? "";

  return {
    ...product,
    productId: String(productId),
    name,
  };
};

/** Category path slugs on a product record (any API shape). */
export const getProductCategoryPath = (product) => {
  if (
    Array.isArray(product?.categoryHierarchy) &&
    product.categoryHierarchy.length
  ) {
    return product.categoryHierarchy;
  }

  const entry = product?.categories?.[0];
  if (entry?.categoryHierarchy?.length) return entry.categoryHierarchy;
  if (Array.isArray(entry) && entry[0]?.slug) return entry;

  return normalizeCategoryHierarchy(product?.categories);
};

const dedupeProducts = (products) => {
  const seen = new Set();
  return products.filter((product) => {
    const id =
      product?.productId ??
      product?.product_id ??
      product?.id ??
      product?.ProductMkt;
    if (id == null || id === "") return true;
    const key = String(id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Products on a category node; מקבץ nodes only include their direct products. */
export const extractProductsForSlugNode = (categoryNode, slug) => {
  if (!categoryNode || !slug) return [];

  const target =
    categoryNode.slug === slug
      ? categoryNode
      : findCategoryNodeBySlug(categoryNode, slug);

  if (!target) return [];

  if (isFamilyCategory(target) || target.isFamily) {
    return dedupeProducts(target.products || []);
  }

  const collected = [];
  const walk = (node) => {
    if (!node) return;
    if (Array.isArray(node.products)) {
      collected.push(...node.products);
    }
    node.children?.forEach((child) => {
      if (isFamilyCategory(child)) return;
      walk(child);
    });
  };
  walk(target);

  return dedupeProducts(collected);
};

/**
 * Keep only products in the exact מחלקה + קבוצה + תת קבוצה (+ מקבץ when isFamily).
 */
export const filterProductsByCategoryLevels = (products, levels) => {
  if (!Array.isArray(products)) return [];

  if (levels?.family) {
    return products.filter((product) => {
      const path = getProductCategoryPath(product);
      if (!path.length) return true;
      return path.some((cat) => cat?.slug === levels.family);
    });
  }

  const required = [levels?.department, levels?.group, levels?.subgroup].filter(
    Boolean,
  );
  if (!required.length) return products;

  return products.filter((product) => {
    if (productIsInFamily(product)) return false;

    const path = getProductCategoryPath(product);
    if (!path.length) return true;

    const slugs = new Set(path.map((cat) => cat?.slug).filter(Boolean));
    return required.every((slug) => slugs.has(slug));
  });
};

export const formatImageDimensions = (media) => {
  const width = media?.width;
  const height = media?.height;
  if (width > 0 && height > 0) return `${width}×${height}`;
  return null;
};

export const formatImageFileSize = (bytes) => {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const mapProductToPeerDefaultImage = (product) => {
  const normalized = normalizeCategoryProduct(product);
  if (!normalized) return null;

  const media = getDefaultMediaItem(normalized.media);
  if (!media?.path) return null;

  return {
    productId: normalized.productId,
    name: normalized.name,
    media,
    dimensions: formatImageDimensions(media),
    fileSize: formatImageFileSize(media.size),
  };
};

export const buildCategoryPeerImages = (products, currentProductId) => {
  if (!Array.isArray(products)) return [];

  const currentId = currentProductId != null ? String(currentProductId) : null;

  return products
    .map(normalizeCategoryProduct)
    .filter((item) => item?.productId && item.productId !== currentId)
    .map(mapProductToPeerDefaultImage)
    .filter(Boolean)
    .sort((a, b) =>
      a.name.localeCompare(b.name, "he", { sensitivity: "base" }),
    );
};
